import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { fmtUsd, DISPLAY_TZ } from '../utils/fmtPrice';
import './AdminMacroChartBundle.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const DAY_LABELS = [
  { v: 0, l: 'Sun' },
  { v: 1, l: 'Mon' },
  { v: 2, l: 'Tue' },
  { v: 3, l: 'Wed' },
  { v: 4, l: 'Thu' },
  { v: 5, l: 'Fri' },
  { v: 6, l: 'Sat' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmailList(text) {
  return [...new Set(String(text || '').split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];
}

function mergeRecipients(existing, incoming) {
  const seen = new Set(existing.map((e) => e.toLowerCase()));
  const next = [...existing];
  for (const email of incoming) {
    if (EMAIL_RE.test(email) && !seen.has(email)) {
      seen.add(email);
      next.push(email);
    }
  }
  return next;
}

/** SQLite datetime('now') is UTC — show Central for operators */
function formatLastSentUtc(sqlUtc) {
  if (!sqlUtc) return '—';
  const normalized = String(sqlUtc).includes('T') ? sqlUtc : `${String(sqlUtc).replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return sqlUtc;
  const central = d.toLocaleString('en-US', {
    timeZone: DISPLAY_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  return `${central} (stored UTC: ${sqlUtc})`;
}

function fmtSignedUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return v >= 0 ? `+$${abs}` : `-$${abs}`;
}

function PlatformStatePreview({ bundle }) {
  const ps = bundle?.platformState;
  const om = ps?.omniMaster;
  if (!om) {
    return (
      <p className="ma-macro-preview-err">
        No <code>platformState</code> in bundle — backend may need restart after deploy.
      </p>
    );
  }

  const outcomes = om.signalOutcomes || {};
  const outcomeCounts = [
    ['filled', outcomes.filled?.length],
    ['pending', outcomes.pending?.length],
    ['failed', outcomes.failed?.length],
    ['rejected', outcomes.rejected?.length],
    ['cancelled', outcomes.cancelled?.length],
  ].filter(([, n]) => n > 0);

  return (
    <div className="ma-macro-platform-preview">
      <p className="ma-macro-platform-meta">
        Bundle <code>{bundle.schemaVersion || '—'}</code>
        {ps.schemaVersion ? ` · platform ${ps.schemaVersion}` : ''}
        {ps.asOf ? ` · asOf ${ps.asOf}` : ''}
        {ps.exportSince ? ` · since last send ${ps.exportSince}` : ''}
      </p>

      {om.bookSource === 'frozen_summary' && (
        <p className="ma-macro-platform-warn">
          <strong>Warning:</strong> {om.bookSourceNote || 'Ledger empty — import MAP baseline on Omni MAP page.'}
        </p>
      )}

      <div className="ma-macro-platform-grid">
        <div>
          <span className="label">Equity</span>
          <strong>{fmtUsd(om.totalEquityUsd)}</strong>
        </div>
        <div>
          <span className="label">Cash deployable</span>
          <strong>{fmtUsd(om.cashDeployableUsd)}</strong>
        </div>
        <div>
          <span className="label">Margin deployed</span>
          <strong>{fmtUsd(om.marginDeployedUsd)}</strong>
        </div>
        <div>
          <span className="label">Deploy %</span>
          <strong>{om.deployPct != null ? `${(om.deployPct * 100).toFixed(1)}%` : '—'}</strong>
        </div>
        <div>
          <span className="label">Realized PnL</span>
          <strong>{fmtUsd(om.realizedPnlUsd)}</strong>
        </div>
        <div>
          <span className="label">Unrealized PnL</span>
          <strong>{fmtUsd(om.unrealizedPnlUsd)}</strong>
        </div>
        <div>
          <span className="label">Paper mode</span>
          <strong>{om.paperMode ? 'Yes' : 'No'}</strong>
        </div>
        <div>
          <span className="label">Book source</span>
          <strong>{om.bookSource || 'ledger'}</strong>
        </div>
        <div>
          <span className="label">Global bot</span>
          <strong>{om.globalBotEnabled ? 'ON' : 'OFF'}</strong>
        </div>
      </div>

      {om.openPositions?.length > 0 ? (
        <>
          <h5>Open positions ({om.openPositions.length})</h5>
          <table className="ma-macro-preview-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Margin</th>
                <th>Lev</th>
                <th>Entry</th>
                <th>Mark</th>
              </tr>
            </thead>
            <tbody>
              {om.openPositions.map((p) => (
                <tr key={`${p.symbol}-${p.side}`}>
                  <td>{p.symbol}</td>
                  <td>{p.side}</td>
                  <td>{fmtUsd(p.marginUsd)}</td>
                  <td>{p.leverage != null ? `${p.leverage}x` : '—'}</td>
                  <td>{p.entryPrice ?? '—'}</td>
                  <td>{p.markPrice ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="ma-macro-platform-empty">No open positions in platformState.</p>
      )}

      {om.marketBrief?.dailyPerformance && !om.marketBrief.dayPnlAvailable && (
        <p className="ma-macro-platform-meta">
          {om.marketBrief.dailyPerformance.note ||
            'Day P&L not available until after the next evening send.'}
        </p>
      )}

      {om.marketBrief?.dailyPerformance?.dayPnlAvailable &&
        om.marketBrief.dailyPerformance.positions?.length > 0 && (
        <>
          <h5>
            Today&apos;s performance (vs last send)
            {om.marketBrief.dailyPerformance.totalDayPnlUsd != null && (
              <>
                {' '}
                · {fmtSignedUsd(om.marketBrief.dailyPerformance.totalDayPnlUsd)} day P&L
                {om.marketBrief.dailyPerformance.totalDayPnlSource === 'equity_delta'
                  ? ' (equity delta)'
                  : ''}
              </>
            )}
          </h5>
          {om.marketBrief.dailyPerformance.dayPnlDiverged && (
            <p className="ma-macro-platform-meta">
              Mark-sum diagnostic{' '}
              {fmtSignedUsd(om.marketBrief.dailyPerformance.openBookMarkDayPnlUsd)} diverged from
              equity delta — use equity delta as truth.
            </p>
          )}
          <table className="ma-macro-preview-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Prior</th>
                <th>Close</th>
                <th>Δ%</th>
                <th>Day P&L</th>
              </tr>
            </thead>
            <tbody>
              {om.marketBrief.dailyPerformance.positions.map((p) => (
                <tr key={`day-${p.symbol}-${p.side}`}>
                  <td>{p.symbol}</td>
                  <td>{p.side}</td>
                  <td>{p.priorMark ?? '—'}</td>
                  <td>{p.currentMark ?? '—'}</td>
                  <td>
                    {p.changePct != null
                      ? `${p.changePct >= 0 ? '+' : ''}${p.changePct}%`
                      : '—'}
                  </td>
                  <td>{fmtSignedUsd(p.dayPnlUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {om.marketBrief?.watchlistMarks?.length > 0 && (
        <>
          <h5>Watchlist / pending closes</h5>
          <table className="ma-macro-preview-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Status</th>
                <th>Close</th>
                <th>Limit</th>
                <th>Δ%</th>
              </tr>
            </thead>
            <tbody>
              {om.marketBrief.watchlistMarks.map((w) => (
                <tr key={`watch-${w.ruleKey}-${w.symbol}`}>
                  <td>{w.symbol}</td>
                  <td>{w.status || '—'}</td>
                  <td>{w.closePrice ?? '—'}</td>
                  <td>{w.limitEntry ?? '—'}</td>
                  <td>
                    {w.changePct != null
                      ? `${w.changePct >= 0 ? '+' : ''}${w.changePct}%`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {om.marketBrief?.priorSnapshotMissing && !om.marketBrief?.dayPnlAvailable && (
        <p className="ma-macro-platform-meta">
          First evening snapshot — day-over-day P&L starts after the next scheduled send.
        </p>
      )}

      {om.fillsSinceLastExport?.length > 0 && (
        <>
          <h5>Fills since last export ({om.fillsSinceLastExport.length})</h5>
          <table className="ma-macro-preview-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Symbol</th>
                <th>Action</th>
                <th>Margin</th>
                <th>Fill</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {om.fillsSinceLastExport.map((f) => (
                <tr key={f.orderId || `${f.symbol}-${f.filledAt}`}>
                  <td>{f.filledAt || '—'}</td>
                  <td>{f.symbol}</td>
                  <td>
                    {f.action} {f.side}
                  </td>
                  <td>{fmtUsd(f.sizeUsd)}</td>
                  <td>{f.fillPrice ?? '—'}</td>
                  <td>{f.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {outcomeCounts.length > 0 && (
        <p className="ma-macro-platform-outcomes">
          Signal outcomes since last send:{' '}
          {outcomeCounts.map(([k, n]) => `${k} ${n}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

export default function AdminMacroChartBundle() {
  const [tab, setTab] = useState('select');
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleTimeCst, setScheduleTimeCst] = useState('07:00');
  const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);

  const fetchBundlePreview = async () => {
    const { data } = await axios.post(
      `${API_BASE}/multi-asset/admin/macro-charts/preview`,
      { selectedSeries: selected },
      { headers: authHeaders() }
    );
    setPreview(data);
    return data;
  };

  const scrollToPreview = () => {
    requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const downloadPreviewJson = () => {
    if (!preview) return;
    const blob = new Blob([JSON.stringify(preview, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omni-macro-charts-${preview.generatedAt?.slice(0, 10) || 'preview'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/admin/macro-charts`, {
        headers: authHeaders(),
      });
      setCfg(data);
      setSelected(data.selectedSeries || []);
      setRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setScheduleEnabled(!!data.scheduleEnabled);
      setScheduleTimeCst(data.scheduleTimeCst || data.scheduleTimeUtc || '07:00');
      setScheduleDays(data.scheduleDays?.length ? data.scheduleDays : [1, 2, 3, 4, 5]);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSeries = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAll = () => {
    setSelected((cfg?.catalog || []).map((s) => s.id));
  };

  const saveSelection = async () => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/macro-charts/selection`,
        { selectedSeries: selected },
        { headers: authHeaders() }
      );
      setCfg(data);
      setMsg(`Saved ${data.selectedSeries.length} selected series.`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const time = String(scheduleTimeCst || '07:00').slice(0, 5);
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/macro-charts/schedule`,
        { scheduleEnabled, scheduleTimeCst: time, scheduleDays },
        { headers: authHeaders() }
      );
      setCfg(data);
      setScheduleTimeCst(data.scheduleTimeCst || time);
      setMsg(
        scheduleEnabled
          ? `Schedule saved — daily send at ${time} Central (CST/CDT).`
          : 'Schedule disabled.'
      );
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveRecipients = async () => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/macro-charts/recipients`,
        { recipients },
        { headers: authHeaders() }
      );
      setCfg(data);
      setRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setMsg(`Saved ${data.recipients.length} recipient(s).`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const addRecipient = () => {
    const parsed = parseEmailList(newEmail);
    if (!parsed.length) {
      setError('Enter a valid email address.');
      return;
    }
    const invalid = parsed.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      setError(`Invalid email: ${invalid[0]}`);
      return;
    }
    setError(null);
    setRecipients((prev) => mergeRecipients(prev, parsed));
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setRecipients((prev) => prev.filter((e) => e !== email));
  };

  const handleRecipientPaste = (e) => {
    const pasted = e.clipboardData?.getData('text');
    if (!pasted || !/[\n,;]/.test(pasted)) return;
    e.preventDefault();
    const parsed = parseEmailList(pasted);
    if (parsed.length) {
      setRecipients((prev) => mergeRecipients(prev, parsed));
      setNewEmail('');
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const data = await fetchBundlePreview();
      const om = data.platformState?.omniMaster;
      const posN = om?.openPositions?.length ?? 0;
      setMsg(
        `Preview ready — ${data.series?.length || 0} series · platformState: equity ${fmtUsd(om?.totalEquityUsd)}, ${posN} open position${posN === 1 ? '' : 's'}.`
      );
      scrollToPreview();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    if (!recipients.length) {
      setError('Add and save at least one recipient before sending.');
      return;
    }
    if (!window.confirm(`Send macro chart bundle now to ${recipients.length} recipient(s)?`)) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/macro-charts/send-now`,
        { recipients, selectedSeries: selected },
        { headers: authHeaders() }
      );
      setCfg(data.config || cfg);
      const sent = data.sentTo?.join(', ') || '—';
      const failed = data.failed?.length
        ? ` Failed: ${data.failed.map((f) => f.email).join(', ')}.`
        : '';
      setMsg(`Email sent to ${sent} (${data.seriesCount} series).${failed}`);
      await load();
      try {
        const bundle = await fetchBundlePreview();
        const om = bundle.platformState?.omniMaster;
        setMsg(
          (m) =>
            `${m} Attachment matches preview: equity ${fmtUsd(om?.totalEquityUsd)}, ${om?.openPositions?.length ?? 0} positions.`
        );
        scrollToPreview();
      } catch {
        /* preview optional after send */
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (d) => {
    setScheduleDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));
  };

  if (loading) return <p className="ma-macro-loading">Loading macro chart settings…</p>;

  const catalog = cfg?.catalog || [];
  const grouped = catalog.reduce((acc, s) => {
    if (!acc[s.group]) acc[s.group] = [];
    acc[s.group].push(s);
    return acc;
  }, {});

  return (
    <section id="ma-macro-charts" className="ma-admin-card ma-macro-charts-card">
      <h3>📈 Macro chart bundle (Claude / MAP discussion)</h3>
      <p className="ma-card-desc">
        Free sources only: <strong>Yahoo Finance</strong> (10Y, gold, VIX, DXY, USD/JPY) and{' '}
        <strong>FRED / Federal Reserve</strong> (10Y TIPS real yield, series DFII10). Optional{' '}
        <code>FRED_API_KEY</code> in backend <code>.env</code> improves real-yield reliability. Email JSON includes{' '}
        <code>series</code> (macro charts) and <code>platformState</code> (Omni MAP book, fills, signal outcomes).
        Use <strong>Preview fetch</strong> below to see the same attachment finance receives.
      </p>

      {error && <div className="error-message ma-admin-error">{error}</div>}
      {msg && <div className="ma-admin-success">{msg}</div>}

      {cfg?.lastSentAt && (
        <p className="ma-macro-meta">
          Last sent: {formatLastSentUtc(cfg.lastSentAt)}
          {cfg.lastError ? ` · Last error: ${cfg.lastError}` : ''}
        </p>
      )}

      <div className="ma-macro-tab-row">
        <button
          type="button"
          className={`ma-macro-tab ${tab === 'select' ? 'active' : ''}`}
          onClick={() => setTab('select')}
        >
          1 · Select series
        </button>
        <button
          type="button"
          className={`ma-macro-tab ${tab === 'schedule' ? 'active' : ''}`}
          onClick={() => setTab('schedule')}
        >
          2 · Schedule
        </button>
        <button
          type="button"
          className={`ma-macro-tab ${tab === 'email' ? 'active' : ''}`}
          onClick={() => setTab('email')}
        >
          3 · Email recipients
        </button>
      </div>

      {tab === 'select' && (
        <div className="ma-macro-panel">
          <div className="ma-macro-panel-actions">
            <button type="button" className="secondary-btn" onClick={selectAll} disabled={busy}>
              Select all
            </button>
            <button type="button" className="primary-btn" onClick={saveSelection} disabled={busy}>
              Save selection
            </button>
          </div>
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="ma-macro-group">
              <h4>{group}</h4>
              {items.map((s) => (
                <label key={s.id} className="ma-checkbox-label ma-macro-series-row">
                  <input
                    type="checkbox"
                    checked={selected.includes(s.id)}
                    onChange={() => toggleSeries(s.id)}
                  />
                  <span>
                    <strong>{s.detail}</strong>
                    <small>{s.note}</small>
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === 'schedule' && (
        <div className="ma-macro-panel">
          <label className="ma-checkbox-label">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
            />
            Enable scheduled email (Central time)
          </label>
          <div className="form-group">
            <label>Send time (Central, 24h — CST/CDT)</label>
            <input
              type="time"
              value={scheduleTimeCst}
              onChange={(e) => setScheduleTimeCst(e.target.value)}
            />
            <span className="field-hint">
              Worker checks each minute; sends once per Central calendar day on selected weekdays.
            </span>
          </div>
          <div className="form-group">
            <label>Days of week (Central)</label>
            <div className="ma-macro-days">
              {DAY_LABELS.map(({ v, l }) => (
                <label key={v} className="ma-macro-day-chip">
                  <input type="checkbox" checked={scheduleDays.includes(v)} onChange={() => toggleDay(v)} />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <button type="button" className="primary-btn" onClick={saveSchedule} disabled={busy}>
            Save schedule
          </button>
        </div>
      )}

      {tab === 'email' && (
        <div className="ma-macro-panel">
          <div className="form-group">
            <label>Add recipients ({recipients.length} saved locally — click Save to persist)</label>
            <div className="ma-macro-recipient-add-row">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onPaste={handleRecipientPaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="you@example.com"
                autoComplete="off"
              />
              <button type="button" className="secondary-btn" onClick={addRecipient} disabled={busy}>
                Add email
              </button>
            </div>
            <span className="field-hint">
              Add one at a time, or paste several separated by commas or new lines. Click Save recipients when done.
            </span>
          </div>
          <div className="ma-macro-recipient-list">
            {recipients.length === 0 ? (
              <p className="ma-macro-recipient-empty">No recipients yet.</p>
            ) : (
              recipients.map((email) => (
                <span key={email} className="ma-macro-recipient-chip">
                  {email}
                  <button type="button" onClick={() => removeRecipient(email)} title="Remove">
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <button type="button" className="primary-btn" onClick={saveRecipients} disabled={busy}>
            Save recipients
          </button>
        </div>
      )}

      <div className="ma-macro-send-row">
        <button type="button" className="secondary-btn" onClick={runPreview} disabled={busy}>
          Preview fetch (no email)
        </button>
        <button type="button" className="primary-btn" onClick={sendNow} disabled={busy}>
          Send bundle now
        </button>
      </div>

      {preview && (
        <div className="ma-macro-preview" ref={previewRef}>
          <div className="ma-macro-preview-header">
            <h4>Email JSON preview (same attachment finance receives)</h4>
            <button type="button" className="secondary-btn" onClick={downloadPreviewJson}>
              Download JSON
            </button>
          </div>

          {preview.series?.length > 0 && (
            <>
              <h5>Macro series ({preview.series.length})</h5>
              <table className="ma-macro-preview-table">
                <thead>
                  <tr>
                    <th>Series</th>
                    <th>Latest</th>
                    <th>Δ</th>
                    <th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.series.map((s) => (
                    <tr key={s.id}>
                      <td>
                        {s.label} · {s.detail}
                      </td>
                      <td>
                        {s.summary.latest} <small>({s.summary.latestDate})</small>
                      </td>
                      <td>{s.summary.change}</td>
                      <td>{s.summary.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.fetchErrors?.length > 0 && (
                <p className="ma-macro-preview-err">
                  Chart fetch errors: {preview.fetchErrors.map((e) => `${e.id}: ${e.error}`).join('; ')}
                </p>
              )}
            </>
          )}

          <h5>platformState.omniMaster</h5>
          <PlatformStatePreview bundle={preview} />
        </div>
      )}
    </section>
  );
}
